import { Core, Decorative } from "@faire/design-tokens";
import { optimizeImageUrlFillSquare } from "@faire/web/common/images/optimizeImages";
import { useLocalization } from "@faire/web/common/localization/useLocalization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { trackEventOnClick } from "@faire/web/common/trackEvent";
import { Button } from "@faire/web/slate/Button/Button";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React, { ComponentType } from "react";
import styled, { css } from "styled-components";

import {
  Carousel as CarouselBase,
  IProps as ICarouselProps,
} from "@faire/retailer/components/Carousel/Carousel";
import { blogPage } from "@faire/retailer/paths";

interface IProps {
  name: string;
  eventPrefix?: string;
  items?: ITestimonial[];
  hideStoryLink?: boolean;
  withRetailerCount?: boolean;
  className?: string;
  maxContentWidth?: number;
  backgroundColor?: string;
}

const TestimonialCarouselBase = React.forwardRef<HTMLDivElement, IProps>(
  (props, ref) => {
    const { backgroundColor } = props;
    const maxHeight = useResponsiveValue({
      mobileAndAbove: undefined,
      tabletAndAbove: "400px",
    });
    const { isMobile } = useViewport();
    const {
      intl: { locale },
    } = useLocalization();

    return (
      <Wrapper
        $backgroundColor={backgroundColor}
        className={props.className}
        ref={ref}
      >
        <Column
          align="center"
          justify="space-evenly"
          style={{
            padding: `${getSpacing("3x", 40)}`,
            height: "100%",
            maxHeight,
          }}
        >
          {props.withRetailerCount ? (
            <>
              <Header>
                <StrictLocalMsg
                  defaultMessage="TRUSTED BY HUNDREDS OF THOUSANDS OF RETAILERS"
                  description={{
                    text: "testimonials header",
                    img: "https://cdn.faire.com/fastly/9a1d255f3d61578599239ed48f24f1b0b3476ee6b2cf1c70b14c5e365081c63d.png",
                  }}
                />
              </Header>
              <Spacer size="3x" />
            </>
          ) : null}
          <Carousel
            name={props.name}
            itemsPerSlide={1}
            padItems
            noWrap={false}
            renderItem={(testimonial, index) => (
              <TestimonialTile
                key={index}
                testimonial={testimonial}
                eventPrefix={props.eventPrefix}
                maxContentWidth={props.maxContentWidth}
                hideStoryLink={props.hideStoryLink}
                isMobile={isMobile}
              />
            )}
            items={props.items ?? getTestimonials(locale)}
          />
        </Column>
      </Wrapper>
    );
  }
);

export const TestimonialCarousel = observer(TestimonialCarouselBase);

const TestimonialTile = ({
  testimonial,
  eventPrefix,
  maxContentWidth,
  hideStoryLink,
  isMobile,
}: {
  testimonial: ITestimonial;
  eventPrefix: string | undefined;
  maxContentWidth: number | undefined;
  hideStoryLink: boolean | undefined;
  isMobile: boolean;
}) => {
  const isSSR = useIsSSR();
  return (
    <Column align="center" justify="center">
      <Avatar $avatarImageUrl={testimonial.avatarImageUrl} $isSSR={isSSR} />
      <Spacer height="2x" />
      <Content $maxContentWidth={maxContentWidth}>
        {isMobile && testimonial.mobileContent
          ? testimonial.mobileContent
          : testimonial.content}
      </Content>
      <Spacer height="3x" />
      <ReviewerInfo>{testimonial.reviewerInfo}</ReviewerInfo>
      {testimonial.blogCta && !hideStoryLink ? (
        <>
          <Spacer height="2x" />
          <Button
            variant="tertiary"
            linkTo={testimonial.blogCta}
            onClick={trackEventOnClick(
              `${eventPrefix}${testimonial.reviewerInfo}`
            )}
          >
            <StrictLocalMsg defaultMessage="Read Their Story" />
          </Button>
        </>
      ) : null}
    </Column>
  );
};

export interface ITestimonial {
  avatarImageUrl: string;
  content: React.ReactNode;
  mobileContent?: React.ReactNode;
  reviewerInfo: string;
  blogCta?: string;
}

const getTestimonials = (locale: string): ITestimonial[] => [
  {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/72f810f95c17f7e377a96d5fa37775a7552c47cfd0cf40074a639b4d80803e05.png",
    content: (
      <StrictLocalMsg defaultMessage="“If you aren’t using Faire, you’re missing a great opportunity. Start today and deal with a very professional company helping all shopkeepers buy quality products from independent makers and artisans.”" />
    ),
    reviewerInfo:
      "Cindy Davenport, Cinport Floral Studio & Gift Designs by Cinport",
  },
  {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/861cfd4023eff50d1fa48db22cbfdf4ce59a05d36634e7aafe86ca022c7bee07.jpeg",
    content: (
      <StrictLocalMsg defaultMessage="“We’ve had such a successful experience with Faire, bringing in new artists and connecting with Shopify; it’s probably the most flawless system we’ve used.”" />
    ),
    reviewerInfo: "Kelly Marie Thompson, Fleur Inc.",
    blogCta: `${blogPage(
      locale
    )}/thestorefront/self-taught-chicago-florist-shares-experience-with-reopening-curbside/`,
  },
  {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/0ded13b33fd5d5c505deb0bd98ec2fb3bc283d2fb09d10ebd82995c4bd326df9.jpeg",
    content: (
      <StrictLocalMsg defaultMessage="“I love Faire because I don’t have to go to trade shows. Faire’s website is 100% more user-friendly and better designed than other wholesale sites.”" />
    ),
    reviewerInfo: "John Frechette, MADE",
    blogCta: `${blogPage(
      locale
    )}/thestorefront/how-this-wyoming-retailer-is-creating-community-during-crisis/`,
  },
  {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/4a1b2e96d3f7904904f8a5bf9d5fc67ff3a6ddb3bc7fbcd0b7b7c6dda81548f3.jpeg",
    content: (
      <StrictLocalMsg defaultMessage="“Faire makes things way simpler. We’ve migrated a number of our producers to the platform and discovered a bunch of new brands that are a great fit for our store.”" />
    ),
    reviewerInfo: "Mat Pond, The Epicurean Trader",
    blogCta: `${blogPage(
      locale
    )}/thestorefront/san-francisco-specialty-grocery-adapts-to-being-essential/`,
  },
];

const Wrapper = styled(Column).attrs({ align: "center", justify: "center" })<{
  $backgroundColor?: string;
}>`
  ${(props) =>
    props.$backgroundColor
      ? css`
          background-color: ${props.$backgroundColor};
        `
      : css`
          background-color: ${Decorative.tint.neutral};
        `}
  width: 100%;
`;

const Header = styled(Typography).attrs({
  color: Core.text.subdued,
  align: "center",
})`
  &&& {
    letter-spacing: 1.25px;
  }
`;

const Carousel = styled(
  CarouselBase as ComponentType<ICarouselProps<ITestimonial>>
)`
  max-width: 1008px;
`;

const Avatar = styled.div<{
  $avatarImageUrl: string;
  $isSSR: boolean;
}>`
  width: 72px;
  height: 72px;
  border-radius: 100%;
  background-position: center;
  background-size: cover;
  background-image: url(${(props) =>
    optimizeImageUrlFillSquare(
      { url: props.$avatarImageUrl },
      props.$isSSR,
      72
    )});
`;

export const Content = styled(Typography).attrs({
  variant: "pageHeaderSerifRegular",
  forwardedAs: "h3",
})<{
  $maxContentWidth?: number;
}>`
  text-align: center;
  ${(props) =>
    props.$maxContentWidth
      ? css`
          max-width: ${props.$maxContentWidth}px;
        `
      : ""}
`;

export const ReviewerInfo = styled(Typography).attrs({
  align: "center",
})``;
